import { useMemo, useState } from "react";
import {
  ArchiveBoxIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { StatusBadges } from "../StatusBadges";
import { setDrawer } from "../../redux-store/features/layoutSlice";
import {
  buildActivityLogSections,
  isArchiveEvent,
} from "../../utils/documentLibraryActivityUtils";
import {
  buildDocumentLibraryActivityNavigation,
  resolveDocumentLibraryActivitySectionTarget,
} from "../../utils/documentLibraryActivityNavigation";

function formatActivityDate(date) {
  return moment.utc(date).format("MMM D, YYYY");
}

function InitialRequestBadge() {
  return (
    <span className="inline-flex items-center rounded-md border border-purple-400 bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
      Initial Request
    </span>
  );
}

const ENTRY_BASE_CLASS =
  "group w-full rounded-md border px-3 py-2 text-left transition-colors";
const ENTRY_INTERACTIVE_CLASS = `${ENTRY_BASE_CLASS} cursor-pointer hover:border-blue-300 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500`;
const ENTRY_STATIC_CLASS = `${ENTRY_BASE_CLASS} border-gray-200 bg-white`;

function ActivityEntryShell({ compact, interactive, onClick, children }) {
  const className = [
    interactive ? ENTRY_INTERACTIVE_CLASS : ENTRY_STATIC_CLASS,
    compact ? "text-xs" : "text-sm",
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = (event) => {
    event.stopPropagation();
    onClick?.(event);
  };

  if (interactive) {
    return (
      <button type="button" onClick={handleClick} className={className}>
        {children}
      </button>
    );
  }

  return <div className={className}>{children}</div>;
}

function RequestActivitySection({
  section,
  compact,
  interactive,
  onNavigate,
}) {
  const { sponsor_name, statusBadge, isInitialRequest, requestedEvent, releasedEvent } =
    section;

  const timelineParts = [];
  if (requestedEvent?.event_date) {
    timelineParts.push(`Requested: ${formatActivityDate(requestedEvent.event_date)}`);
  }
  if (releasedEvent?.event_date) {
    timelineParts.push(`Released: ${formatActivityDate(releasedEvent.event_date)}`);
  }

  return (
    <ActivityEntryShell
      compact={compact}
      interactive={interactive}
      onClick={onNavigate}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={`font-semibold ${
            interactive
              ? "text-gray-900 group-hover:text-blue-600"
              : "text-gray-900"
          }`}
        >
          {sponsor_name}
        </span>
        <StatusBadges statusName={statusBadge} />
        {isInitialRequest ? <InitialRequestBadge /> : null}
      </div>
      {timelineParts.length > 0 ? (
        <p
          className={`mt-1 text-gray-500 ${
            interactive ? "group-hover:text-blue-700/80" : ""
          }`}
        >
          {timelineParts.join(" ")}
        </p>
      ) : null}
    </ActivityEntryShell>
  );
}

function ArchiveActivitySection({
  section,
  compact,
  interactive,
  onNavigate,
}) {
  const { item } = section;
  const isArchived = item?.event_type === "Archived";
  // Prefer the user who archived (actor), never the vendor/sponsor org name.
  const name = item?.actor || item?.activity_done_by || "Diligent Admin";

  return (
    <ActivityEntryShell
      compact={compact}
      interactive={interactive}
      onClick={onNavigate}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <ArchiveBoxIcon
          className="h-4 w-4 shrink-0 text-gray-500"
          aria-hidden="true"
        />
        <span
          className={`font-semibold ${
            interactive
              ? "text-gray-900 group-hover:text-blue-600"
              : "text-gray-900"
          }`}
        >
          {name}
        </span>
        <StatusBadges statusName={item?.status} />
      </div>
      {item?.event_date ? (
        <p
          className={`mt-1 text-gray-500 ${
            interactive ? "group-hover:text-blue-700/80" : ""
          }`}
        >
          {isArchived ? "Archived on " : "Unarchived on "}
          {formatActivityDate(item.event_date)}
        </p>
      ) : null}
      {isArchiveEvent(item?.event_type) && item?.archive_comment ? (
        <p
          className={`mt-1 break-words text-gray-500 italic ${
            interactive ? "group-hover:text-blue-700/80" : ""
          }`}
        >
          &ldquo;{item.archive_comment}&rdquo;
        </p>
      ) : null}
    </ActivityEntryShell>
  );
}

export default function DocumentLibraryActivityLog({
  items = [],
  activityNavigation = null,
  compact = false,
  loading = false,
  entityType,
  hcQuestionnaireId,
  vqaRequestId,
  providerName: _providerName,
  documentStatus,
  archiveComment,
  archivedBy,
  archivedOn,
  closeDrawerOnNavigate = false,
  showTopBorder = true,
  defaultOpen = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fallbackArchive = useMemo(() => {
    if (documentStatus !== "Archived") {
      return null;
    }

    return {
      event_type: "Archived",
      status: "Archived",
      archive_comment: archiveComment || undefined,
      // User who archived — never the vendor org name.
      actor: archivedBy || undefined,
      event_date: archivedOn || undefined,
    };
  }, [archiveComment, archivedBy, archivedOn, documentStatus]);

  const sections = useMemo(
    () => buildActivityLogSections(items, fallbackArchive),
    [fallbackArchive, items]
  );

  const documentNavigation = useMemo(
    () =>
      buildDocumentLibraryActivityNavigation({
        activityNavigation,
        entityType,
        hcQuestionnaireId,
        vqaRequestId,
      }),
    [activityNavigation, entityType, hcQuestionnaireId, vqaRequestId]
  );

  const sectionNavigationTargets = useMemo(
    () =>
      sections.map((section) =>
        resolveDocumentLibraryActivitySectionTarget(section, documentNavigation)
      ),
    [documentNavigation, sections]
  );

  const handleNavigate = (target) => (event) => {
    event?.stopPropagation?.();

    if (!target) {
      return;
    }

    if (closeDrawerOnNavigate) {
      dispatch(setDrawer(false));
    }

    navigate(target);
  };

  const stopCardClick = (event) => {
    event.stopPropagation();
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500 italic py-2">Loading activity…</div>
    );
  }

  if (!sections.length) {
    return null;
  }

  return (
    <div
      className={[
        compact ? "mt-2" : "mt-4",
        showTopBorder ? "border-t border-gray-200 pt-3" : "pt-2",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={stopCardClick}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <h4 className="text-sm font-semibold text-custom-dark-grey">
          View Activity Log ({sections.length})
        </h4>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen ? (
        <div className="mt-3 space-y-2">
          {sections.map((section, index) => {
            const sectionTarget = sectionNavigationTargets[index];
            const interactive = Boolean(sectionTarget);

            if (section.type === "request") {
              const key =
                section.document_request_id ||
                `${section.sponsor_name}-${section.sortDate}-${index}`;
              return (
                <RequestActivitySection
                  key={key}
                  section={section}
                  compact={compact}
                  interactive={interactive}
                  onNavigate={handleNavigate(sectionTarget)}
                />
              );
            }

            const key = `${section.item?.event_type}-${section.item?.event_date}-${index}`;
            return (
              <ArchiveActivitySection
                key={key}
                section={section}
                compact={compact}
                interactive={interactive}
                onNavigate={handleNavigate(sectionTarget)}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
